/* The page's side of the engine: a Web Worker made from this bundle's own URL.
 *
 * **Same file, same URL, so there is nothing new to fetch or to version.**
 * `import.meta.url` is the bundle as the page loaded it, `?v=` and all, and the
 * browser serves the second load from cache; `tester.js` looks at whether
 * `document` exists to decide which half of itself to run. The cost is one
 * module worker, which every current browser has had for years, and the page
 * shows the engine's own start-up error on one that has not.
 *
 * **Latest wins, and at most one request waits.** Typing sends an analyse per
 * debounced keystroke, and a heavy pattern can take longer than the debounce.
 * Queueing every keystroke would have the worker still answering questions
 * the page stopped asking; so one request is in flight, one is waiting, and a
 * newer one replaces the waiting one, whose caller is told `null` and moves
 * on. A trace is asked for by a button press and is simply sent.
 */

let worker = null;
let ready = null;
let sequence = 0;
const waiting = new Map();

/** Resolves with the engine's version once the worker has instantiated it. */
export function start() {
  ready ??= new Promise((resolve, reject) => {
    worker = new Worker(import.meta.url, { type: "module" });

    worker.onmessage = (event) => {
      const message = event.data;

      if ("ready" in message) {
        if (message.ready) {
          resolve(message.version);
        } else {
          reject(new Error(message.error));
        }

        return;
      }

      const settle = waiting.get(message.id);
      waiting.delete(message.id);
      settle?.(message);
    };

    worker.onerror = (event) => {
      reject(new Error(event.message ?? "the worker failed"));
    };
  });

  return ready;
}

function ask(request) {
  return new Promise((resolve) => {
    const id = ++sequence;
    waiting.set(id, resolve);
    worker.postMessage({ id, ...request });
  });
}

let inFlight = false;
let queued = null;

/**
 * The match and the enumeration for one state of the form, or `null` if a
 * newer state replaced this one before it was sent.
 */
export function analyse(pattern, subject, flags) {
  return new Promise((resolve) => {
    queued?.resolve(null);
    queued = { request: { kind: "analyse", pattern, subject, flags }, resolve };
    pump();
  });
}

function pump() {
  if (inFlight || queued === null) {
    return;
  }

  const { request, resolve } = queued;
  queued = null;
  inFlight = true;

  ask(request).then((reply) => {
    inFlight = false;
    resolve(reply);
    pump();
  });
}

/** The step debugger's run. Sent as asked; the worker answers in order. */
export function trace(pattern, subject, flags) {
  return ask({ kind: "trace", pattern, subject, flags });
}
